import { useEffect, useMemo, useState } from "react";
import { listEndpoints } from "../../lib/reportClient.js";
import { EYEBROW_CLASS, NOTE_CLASS } from "../../lib/ui.js";
import { plural } from "./StepStrip.jsx";
import { INPUT_CLASS, PRIMARY_ACTION, SECONDARY_ACTION } from "./styles.js";
import { SectionCard } from "./controls.jsx";
import { Blocker } from "./textRoles.jsx";

const PAGE_SIZE = 100;
const SEARCH_DELAY_MS = 250;

function endpointName(endpoint) {
    return endpoint.hostname || endpoint.id;
}

/// Searchable endpoint list. The search matches hostname, serial, user, OS,
/// and Origin Agent version on the server. Ids stay base64 as the API sends
/// them, because the pilot RPCs take them back as protobuf bytes.
export function useEndpointSearch(query) {
    const [result, setResult] = useState({ endpoints: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    useEffect(() => {
        let live = true;
        setLoading(true);
        const run = () => {
            listEndpoints({ page: 0, pageSize: PAGE_SIZE, hostname: query.trim() || undefined })
                .then((response) => {
                    if (!live) return;
                    setResult({ endpoints: response.endpoints, total: response.total });
                    setError("");
                })
                .catch(() => { if (live) setError("Endpoints could not be listed. Try the search again."); })
                .finally(() => { if (live) setLoading(false); });
        };
        if (!query.trim()) {
            run();
            return () => { live = false; };
        }
        const timer = setTimeout(run, SEARCH_DELAY_MS);
        return () => { live = false; clearTimeout(timer); };
    }, [query]);
    return { ...result, loading, error };
}

export function EndpointSearchTable({ query, onQueryChange, search, renderAction }) {
    return (
        <div className="grid gap-[8px]">
            <label className="grid gap-[5px]">
                <span className="text-[11px] font-medium text-[color:var(--ink)]">Search endpoints</span>
                <input className={INPUT_CLASS} value={query} onChange={(event) => onQueryChange(event.target.value)} placeholder="Hostname, user, OS, or Origin Agent version" />
            </label>
            {search.error ? <p role="alert" className="m-0 text-[11px] text-[#aa4c43]">{search.error}</p> : null}
            <div className="overflow-x-auto border border-[color:var(--line)]" aria-busy={search.loading ? "true" : "false"}>
                <table className="w-full min-w-[520px] border-collapse text-left text-[11px]">
                    <thead className="bg-[color:var(--control-bg)] text-[9px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]">
                        <tr><th className="w-[40px] px-[10px] py-[7px] font-normal"><span className="sr-only">Selection</span></th><th className="px-[10px] py-[7px] font-normal">Endpoint</th><th className="px-[10px] py-[7px] font-normal">OS</th><th className="px-[10px] py-[7px] font-normal">Origin Agent</th></tr>
                    </thead>
                    <tbody>
                        {search.endpoints.map((endpoint) => (
                            <tr key={endpoint.id} className="border-t border-[color:var(--line)]">
                                <td className="px-[10px] py-[7px]">{renderAction(endpoint)}</td>
                                <td className="px-[10px] py-[7px] text-[color:var(--ink)]">{endpointName(endpoint)}</td>
                                <td className="px-[10px] py-[7px] text-[color:var(--muted)]">{endpoint.platform}</td>
                                <td className="px-[10px] py-[7px] text-[color:var(--muted)]">{endpoint.agentVersion}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {!search.loading && !search.endpoints.length && !search.error ? <p className="m-0 p-[16px] text-center text-[11px] text-[color:var(--muted)]">No endpoint matches this search.</p> : null}
            </div>
            {search.total > search.endpoints.length ? <p className={`${NOTE_CLASS} m-0`}>{`${search.endpoints.length.toLocaleString()} of ${plural(search.total, "endpoint")} listed. Search to list others.`}</p> : null}
        </div>
    );
}

function groupCounts(endpoints, key) {
    const counts = new Map();
    for (const endpoint of endpoints) {
        const value = endpoint[key];
        if (!value) continue;
        counts.set(value, (counts.get(value) ?? 0) + 1);
    }
    return counts;
}

/// Coverage of the listed endpoints by OS and by Origin Agent version, so the
/// administrator can see which groups the pilot leaves out.
export function pilotCoverage(listed, selected) {
    const rows = [];
    for (const [key, prefix] of [["platform", ""], ["agentVersion", "Origin Agent "]]) {
        const all = groupCounts(listed, key);
        const chosen = groupCounts(selected, key);
        for (const [value, count] of [...all.entries()].sort((a, b) => b[1] - a[1])) {
            rows.push({ group: `${prefix}${value}`, listed: count, inPilot: chosen.get(value) ?? 0 });
        }
    }
    return rows;
}

export default function ChoosePilotEndpoints({ selected, onSelectedChange, limit, preview, currentGeneration, busy, onBack, onStart }) {
    const [query, setQuery] = useState("");
    const search = useEndpointSearch(query);
    const selectedIds = useMemo(() => new Set(selected.map((endpoint) => endpoint.id)), [selected]);
    const atLimit = limit > 0 && selected.length >= limit;
    const toggle = (endpoint) => onSelectedChange(selectedIds.has(endpoint.id)
        ? selected.filter((item) => item.id !== endpoint.id)
        : [...selected, endpoint]);
    const listed = useMemo(() => {
        const byId = new Map(search.endpoints.map((endpoint) => [endpoint.id, endpoint]));
        for (const endpoint of selected) if (!byId.has(endpoint.id)) byId.set(endpoint.id, endpoint);
        return [...byId.values()];
    }, [search.endpoints, selected]);
    const coverage = useMemo(() => pilotCoverage(listed, selected), [listed, selected]);
    const uncovered = coverage.filter((row) => row.group.startsWith("Origin Agent ") && !row.inPilot);
    const startReason = !selected.length
        ? "Select at least one endpoint."
        : limit > 0 && selected.length > limit ? `Select at most ${plural(limit, "endpoint")}.` : "";
    return (
        <div className="grid items-start gap-[16px] xl:grid-cols-[minmax(0,1fr)_330px]">
            <section className="grid min-w-0 gap-[12px] border border-[color:var(--line)] bg-[color:var(--bg)] p-[14px]" aria-label="Choose pilot endpoints">
                <div className="grid gap-[3px]">
                    <h2 className="m-0 text-[15px] leading-[1.1] font-medium tracking-[-0.03em] text-[color:var(--ink)]">Choose pilot endpoints</h2>
                    <p className={`${NOTE_CLASS} m-0`}>{currentGeneration && currentGeneration !== "0"
                        ? `These endpoints receive the draft. All other endpoints stay on v${currentGeneration} until you sign the release.`
                        : "These endpoints receive the draft. No other endpoint receives a policy until you sign the release."}</p>
                </div>
                {selected.length ? (
                    <div className="grid gap-[6px]">
                        <span className={EYEBROW_CLASS}>{limit > 0 ? `${selected.length.toLocaleString()} of ${limit.toLocaleString()} selected` : `${selected.length.toLocaleString()} selected`}</span>
                        <div className="flex flex-wrap gap-[5px]">
                            {selected.map((endpoint) => (
                                <span key={endpoint.id} className="inline-flex min-h-[26px] items-center gap-[7px] border border-[color:var(--line)] bg-[color:var(--control-raised-bg)] px-[7px] text-[10px]">
                                    {endpointName(endpoint)}
                                    <button type="button" aria-label={`Remove ${endpointName(endpoint)}`} className="border-0 bg-transparent text-[color:var(--muted)]" onClick={() => toggle(endpoint)}>x</button>
                                </span>
                            ))}
                        </div>
                    </div>
                ) : null}
                <EndpointSearchTable
                    query={query}
                    onQueryChange={setQuery}
                    search={search}
                    renderAction={(endpoint) => (
                        <input
                            type="checkbox"
                            aria-label={`Select ${endpointName(endpoint)}`}
                            checked={selectedIds.has(endpoint.id)}
                            disabled={!selectedIds.has(endpoint.id) && atLimit}
                            onChange={() => toggle(endpoint)}
                        />
                    )}
                />
                {atLimit ? <p className={`${NOTE_CLASS} m-0`}>{`A pilot holds at most ${plural(limit, "endpoint")}.`}</p> : null}
            </section>
            <aside className="grid content-start gap-[12px] xl:sticky xl:top-[12px]">
                <section className="grid gap-[8px] border border-[color:var(--line)] bg-[color:var(--bg)] p-[14px]" aria-label="Coverage">
                    <span className={EYEBROW_CLASS}>Coverage</span>
                    {coverage.length ? (
                        <table className="w-full border-collapse text-left text-[11px]">
                            <thead className="text-[10px] text-[color:var(--muted)]"><tr><th className="py-[4px] font-normal">Group</th><th className="py-[4px] text-right font-normal">Listed</th><th className="py-[4px] text-right font-normal">In pilot</th></tr></thead>
                            <tbody>
                                {coverage.map((row) => (
                                    <tr key={row.group} className="border-t border-[color:var(--line)]">
                                        <td className="py-[4px] text-[color:var(--ink)]">{row.group}</td>
                                        <td className="py-[4px] text-right text-[color:var(--muted)]">{row.listed.toLocaleString()}</td>
                                        <td className={`py-[4px] text-right ${row.inPilot ? "text-[color:var(--green)]" : "text-[#aa742f]"}`}>{row.inPilot.toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : null}
                    {selected.length ? uncovered.map((row) => <p key={row.group} className="m-0 text-[11px] text-[#8a5a23]">{`No pilot endpoint runs ${row.group}.`}</p>) : null}
                </section>
                <SectionCard eyebrow="Reachability preview" title={`${preview?.reachable ?? 0} of ${preview?.total ?? 0} endpoints`} description="This estimates where the policy can be delivered. It does not predict apply failures or later drift.">
                    <div aria-busy={preview ? "false" : "true"} className="grid gap-[8px]">
                        <div className="h-[3px] bg-[color:var(--gray-fill)]"><span className="block h-full bg-[color:var(--green)]" style={{ width: `${preview?.total ? (preview.reachable / preview.total) * 100 : 0}%` }} /></div>
                        <div className="grid gap-[8px] text-[10px] text-[color:var(--muted)]"><p className="m-0"><strong className="font-medium text-[color:var(--ink)]">{preview?.shadowed ?? 0} shadowed</strong> by a higher-priority policy source.</p><p className="m-0"><strong className="font-medium text-[color:var(--ink)]">{preview?.unsupported ?? 0} unsupported</strong> until their Origin Agent is updated.</p></div>
                        {preview?.rejections?.length ? <Blocker>The server rejected this composed draft. Correct the reported controls before you start a pilot.</Blocker> : null}
                    </div>
                </SectionCard>
                <div className="grid gap-[8px] border border-[color:var(--line)] bg-[color:var(--bg)] p-[14px]">
                    <button type="button" className={`${PRIMARY_ACTION} min-h-[38px]`} disabled={busy || Boolean(startReason)} onClick={onStart}>{busy ? "Starting pilot..." : selected.length ? `Start pilot on ${plural(selected.length, "endpoint")}` : "Start pilot"}</button>
                    {startReason ? <p className="m-0 text-[11px] text-[#8a5a23]">{startReason}</p> : <p className={`${NOTE_CLASS} m-0`}>Pilot endpoints receive the draft at their next check-in.</p>}
                    <button type="button" className={SECONDARY_ACTION} disabled={busy} onClick={onBack}>Back to draft</button>
                </div>
            </aside>
        </div>
    );
}
